import axios from 'axios';
import { toast } from 'react-toastify';

import { initializeAxiosMockAdapter, isMockEnabled } from '../mocks/api';
import { clearLegacyTokenCache, resolveApiAccessToken } from '../utils/token-resolver';

const API_LIMIT = 10;
const API_URL = process.env.REACT_APP_BASQET_API_BASE_URL || '';
const API_VERSION = 1;
const API_MOCK_ENABLED = process.env.REACT_APP_API_MOCK_ENABLED === 'true';

const IS_ADMIN = process.env.REACT_APP_IS_ADMIN === 'true';
const APP_NAME = 'basqet';

// Cloudflare Access service token. CRA only inlines REACT_APP_* vars, so the
// unprefixed names are read only as a fallback for non-browser callers.
const CF_ACCESS_CLIENT_ID =
  process.env.REACT_APP_CF_ACCESS_CLIENT_ID || process.env.CF_ACCESS_CLIENT_ID || '';
const CF_ACCESS_CLIENT_SECRET =
  process.env.REACT_APP_CF_ACCESS_CLIENT_SECRET || process.env.CF_ACCESS_CLIENT_SECRET || '';

const applyCloudflareAccessHeaders = (config) => {
  if (!CF_ACCESS_CLIENT_ID || !CF_ACCESS_CLIENT_SECRET) return config;
  config.headers['CF-Access-Client-Id'] = CF_ACCESS_CLIENT_ID;
  config.headers['CF-Access-Client-Secret'] = CF_ACCESS_CLIENT_SECRET;
  return config;
};

const getRawIdpToken = () => {
  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${
      process.env.REACT_APP_OIDC_AUTHORITY || 'https://q-accounts.qdx.global/realms/quidax'
    }:${process.env.REACT_APP_OIDC_CLIENT_ID || 'fe-client'}`
  );
  if (!oidcStorage) return '';

  const user = JSON.parse(oidcStorage);
  return user?.access_token || '';
};

const getLegacyToken = () => {
  const stored = sessionStorage.getItem(APP_NAME);
  if (stored) {
    const { token } = JSON.parse(stored);
    return token || '';
  }
  return '';
};

const getToken = () => {
  const idpToken = getRawIdpToken();
  if (idpToken) return `Bearer ${idpToken}`;

  const legacyToken = getLegacyToken();
  return legacyToken ? `Bearer ${legacyToken}` : '';
};

const instance = axios.create({
  baseURL: (API_URL
    ? `${API_URL}${API_URL.endsWith('/') ? '' : '/'}v${API_VERSION}${IS_ADMIN ? '/admin' : ''}`
    : `/v${API_VERSION}${IS_ADMIN ? '/admin' : ''}`
  ).replace(/^\/+/, '/'),
  crossdomain: true,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    Authorization: getToken()
  }
});

if (isMockEnabled()) {
  initializeAxiosMockAdapter(instance);
}

instance.interceptors.request.use(async (config) => {
  applyCloudflareAccessHeaders(config);

  const idpToken = getRawIdpToken();
  if (idpToken) {
    try {
      const resolvedToken = await resolveApiAccessToken(idpToken);
      config.headers.Authorization = `Bearer ${resolvedToken}`;
    } catch (error) {
      console.error('Token resolution failed', error);
      return Promise.reject(error);
    }
  } else {
    const legacyToken = getLegacyToken();
    if (legacyToken) {
      config.headers.Authorization = `Bearer ${legacyToken}`;
    }
  }
  return config;
});

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    if (error.message === 'Network Error') {
      return Promise.reject({
        message: error.message
      });
    } else if (401 === error.response?.status) {
      clearLegacyTokenCache();
      await sessionStorage.clear();
      await toast.error('Session expired', {
        toastId: 'session-expired'
      });
      setTimeout(() => {
        window.location.replace('/login');
      }, 2000);

      /*
       * The handleQueryError function uses the rejection reason below to
       * prevent toasts from showing up for *subsequent API calls after session has expired
       */
      return Promise.reject({
        message: ''
      });
    } else {
      if (!error?.config?.suppressErrorLog) {
        const safeContext = {
          message: error?.message,
          status: error?.response?.status,
          url: error?.config?.url,
          method: error?.config?.method
        };
        console.log('Error occurred while making API calls', safeContext);
      }
      return Promise.reject(error);
    }
  }
);

export {
  API_LIMIT,
  API_MOCK_ENABLED,
  API_URL,
  API_VERSION,
  applyCloudflareAccessHeaders,
  CF_ACCESS_CLIENT_ID,
  CF_ACCESS_CLIENT_SECRET,
  instance,
  IS_ADMIN
};
